import {Component, DestroyRef, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {lastValueFrom, Observable} from 'rxjs';
import {Product} from '../Model/Catalog/Product';
import {NextPimField} from '../Model/Catalog/NextPimField';
import {FieldNames} from '../Model/FieldNames';
import {Message} from '../Model/System/Message';
import {ExcelElement} from '../Model/ui/ExcelElement';
import {Functions} from '../Services/functions';
import {ModelService} from '../Services/model.service';
import {SystemService} from '../Services/system.service';
import {TemplateService} from '../Services/template.service';
import {ValidFeatureService} from '../Services/validFeature.service';
import {custom} from "devextreme/ui/dialog";
import {NpInputControlContext} from "../ui/inputControl/np-input-control-context";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
    selector: 'template-editor',
    templateUrl: './template-editor.component.html',
    styleUrls: ['./template-editor.component.css'],
    providers: [{provide: NpInputControlContext, useClass: NpInputControlContext}],
    standalone: false
})
export class NewTemplateEditorComponent implements OnInit, OnDestroy, OnChanges {
    @Input() showSelector: boolean = false;
    @Input() exportMode: boolean = false;
    @Input() templateChange: Observable<string>;
    @Input() exportVersion: string;

    backupProduct: Product = new Product();
    _excelModel: ExcelElement[];
    get excelModel(): ExcelElement[] {
        return this._excelModel;
    }

    set excelModel(value: ExcelElement[]) {
        this._excelModel = value;
    }

    messageSpidIntPidDefaultNotAllowed: string = '';
    messageSelectTemplate: string = '';
    private fieldNames: FieldNames;
    selectedElements: string[] = [''];
    private context = inject(NpInputControlContext);

    _product: Product = new Product();

    private destroyRef = inject(DestroyRef);

    get product(): Product {
        return this._product;
    }

    set product(product: Product) {
        this._product = this.modelService.productService.getProductFromJson(
            product,
            this.modelService.loginService.wawiSettings
        );
        this.product.linkedFields = [];
        this.backupProduct = this.modelService.productService.getProductFromJson(
            product,
            this.modelService.loginService.wawiSettings
        );
        this.backupProduct.linkedFields = [];
        this.buildListsAndTables();
    }

    constructor(
        public modelService: ModelService,
        public systemService: SystemService,
        public templateService: TemplateService,
        public translate: TranslateService,
        public validFeatureService: ValidFeatureService
    ) {
        this.fieldNames = new FieldNames();
        this.messageSpidIntPidDefaultNotAllowed = this.translate.instant('messageSpidIntPidDefaultNotAllowed');
        this.messageSelectTemplate = this.translate.instant('Bitte ein Template auswählen') + '.';
        this.clearPimField = this.clearPimField.bind(this);
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.updateInputControlContext();
    }

    private updateInputControlContext() {
        this.context.model = this.product;
        this.context.selectedElements = this.selectedElements;
        this.context.showDragHandle = this.exportMode;
        this.context.templateView = true;
        this.context.excelModel = this.excelModel;
    }

    private subscribeToContextEvents() {
        this.context.valueChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => this.addDefaultElement(x));
        this.context.elementSelected.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => this.elementSelectFunction(x));
        this.context.elementDropped.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => this.excelElementDrop(x));
        this.context.pimFieldReordered.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => this.updateTemplate(x));
        this.context.pimFieldCleared.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => this.clearPimField(x));
    }

    fillProducts() {
        if (!this.exportMode && this.product == null) {
            this.product = this.modelService.productService.getProductFromJson(
                new Product(),
                this.modelService.loginService.wawiSettings
            );
            this.backupProduct = this.modelService.productService.getProductFromJson(
                new Product(),
                this.modelService.loginService.wawiSettings
            );
        }

        if (this.modelService.catalogService.product) {
            this.product = this.modelService.productService.getProductFromJson(
                this.modelService.catalogService.product,
                this.modelService.loginService.wawiSettings
            );
            this.product.linkedFields = [];
            this.backupProduct = this.modelService.productService.getProductFromJson(
                this.modelService.catalogService.product,
                this.modelService.loginService.wawiSettings
            );
            this.backupProduct.linkedFields = [];
            return;
        }

        this.getFirstProduct();
    }

    private _isVisible: boolean = true;

    get isVisible(): boolean {
        return this._isVisible;
    }

    set isVisible(value: boolean) {
        this._isVisible = value;
    }

    get isCifExport() {
        return this.exportVersion === 'CIF_V3';
    }

    get template() {
        return this.templateService.template;
    }

    get catalog() {
        return this.modelService.catalogService.catalog;
    }

    get customerId() {
        return this.modelService.loginService.currentUser.customerId;
    }

    ngOnInit(): void {
        if (this.templateChange != null) {
            this.templateChange.subscribe((templateId: string) => {
                if (templateId != null) {
                    this.templateService.getTemplateById(templateId).subscribe(() => {
                        this.templateSelected(null);
                    });
                } else {
                    this.templateSelected(null);
                }
            });
        }

        this.excelModel = [];
        if (!this.templateService?.template?.useMetadata) {
            this.modelService.catalogService.useMetadata = null;
        } else {
            this.modelService.catalogService.useMetadata = true;
        }

        if (this.modelService.catalogService.catalog == null ||
            this.modelService.catalogService.catalog.id == null ||
            this.modelService.catalogService.catalog.id == ''
        ) {
            this.modelService.catalogService.catalog = this.modelService.catalogService.catalogs[0];
        }

        if (this.templateService.importJob != null) {
            this.templateService.getTemplateById(this.templateService.importJob.templateId).subscribe(() => {
                this.templateSelected(null);
            });
        } else {
            if (this.templateService.template != null) {
                this.exportMode = this.templateService.template.isExportTemplate;
            }

                this.templateSelected(null);

        }
        this.updateInputControlContext();
        this.subscribeToContextEvents();

        this.context.excelModel = this.excelModel;
        this.context.templateUpdated.emit();
    }

    ngOnDestroy(): void {
        this.templateService.importJob = null;
        this.templateService.template = null;
        this.selectedElements = [''];

        this.excelModel = [];
        this.isVisible = true;
    }

    public elementSelectFunction(e) {
        this.selectedElements = [e];
        this.updateInputControlContext();
        this.context.selectedElementsChanged.emit(true);
    }

    public excelRowSelected(e) {
        if (e == '') {
            this.selectedElements = [''];
            return;
        }

        var element = this.excelModel.filter((element) => element.id == e).shift();
        if (element == null) {
            return;
        }

        this.selectedElements = this.templateService.createIdentifiers(element.pimFields);
        this.updateInputControlContext();
        this.context.selectedElementsChanged.emit(false);
    }

    public getFirstProduct() {
        let customerId = '';
        customerId = this.modelService.loginService.currentUser.customerId;

        this.modelService.catalogService.getFirstProduct(customerId).subscribe((result: Product) => {
            this.product = result;
            this.templateSelected(null);
        });
    }

    switchMetadataVisibility() {
        this.modelService.catalogService.useMetadata = !this.modelService.catalogService.useMetadata;
    }


    public onDropAdd(e) {
        if (e.itemData.source == 'excelModel') {
            return;
        }

        this.addElement(e.itemData.dField, e.itemData.dSystem, e.itemData.dElement);
    }

    public onDropRemove(e) {
        if (e.itemData.source != 'excelModel') {
            return;
        }

        this.removeElement(e.itemData.dField, e.itemData.dSystem, e.itemData.dElement, e.itemData);
    }

    public excelElementDrop(e) {
        let field = e.newField;
        let system = e.newSystem;
        let element = e.newElement;
        this.addElementFromExcel(field, system, element, e.itemData);
    }

    public elementDelete(e) {
        let fields = e.dFields;
        let detailFields = e.dDetailFields;
        let system = e.dSystem;
        let element = e.dElement;

        let needsUpdate = false;

        for (var field of fields) {
            let found = this.excelModel.find(
                (i) => i.pimFields.findIndex((x) => x.field == field && x.systemKey == system && x.elementKey == element) != -1
            );
            needsUpdate = this.removeOrClearExcelElement(found, needsUpdate);
        }

        if (detailFields != null) {
            for (let field of detailFields) {
                let found = this.excelModel.filter(
                    (i) =>
                        i.pimFields.findIndex(
                            (x) => x.field == field && x.systemKey == system && (x.elementKey == element || element == '-1')
                        ) != -1
                );

                for (let item of found) {
                    needsUpdate = this.removeOrClearExcelElement(item, needsUpdate);
                }
            }
        }

        if (needsUpdate) {
            this.excelModel = [...this.excelModel];
            this.updateTemplate(fields);
        }
    }

    public removeOrClearExcelElement(excelElement: ExcelElement, needsUpdate: boolean): boolean {
        let index = this.excelModel.indexOf(excelElement);
        if (index >= 0) {
            if (
                excelElement.title == null ||
                excelElement.title == undefined ||
                excelElement.title == '' ||
                this.exportMode
            ) {
                this.excelModel.splice(index, 1);
                this.excelModel = [...this.excelModel];
            } else {
                excelElement.pimFields = [];
            }

            needsUpdate = true;
        }

        return needsUpdate;
    }

    public async addDefaultElement(event) {
        if (event == null || event.event == undefined) return;

        let field = event.dField;
        let system = event.dSystem;
        let element = event.dElement;
        let value = event.value;

        if (field == null || field == undefined) return;

        if (
            field == this.fieldNames.supplierPid ||
            field == this.fieldNames.internatonalPid ||
            field == this.fieldNames.supplierInternationalId ||
            field == this.fieldNames.pid
        ) {
            this.systemService.notify(new Message(this.messageSpidIntPidDefaultNotAllowed, 'info'), 3000);
            return;
        }

        if (field == this.fieldNames.featureSystemOrder || field == this.fieldNames.fOrder) {
            if (system.length != 24) {
                this.templateService.changeKey(
                    this.product,
                    event,
                    system,
                    element,
                    field,
                    this.excelModel
                );
                this.updateTemplate(field);
                return;
            }
        }

        if (
            field == this.fieldNames.featureSystemName ||
            field == this.fieldNames.fName ||
            field == this.fieldNames.fUnit
        ) {
            this.templateService.changeKey(this.product, event, system, element, field, this.excelModel);
        }

        if (field == 'MIME_ORDER' || field == 'PRICE_LIST_ORDER' || field == 'PRICE_ORDER' || field == this.templateService.fieldNames.packingOrder ||
            field == this.fieldNames.supplierOrder) {
            this.templateService.changeKey(this.product, event, system, element, field, this.excelModel);
            this.updateTemplate(field);
            return;
        }

        if (!event.createTemplateItem) {
            return;
        }

        this.templateService.refresExcelElementhOrder(this.excelModel);

        let count = this.excelModel.length;

        let excelElement = new ExcelElement();

        if (this.templateService.hasExcelElement(this.excelModel, field, system, element)) {
            excelElement = this.templateService.getExcelElement(this.excelModel, field, system, element);

            if (this.exportMode) this.templateService.clearPreviewRow1Row2(excelElement);

            this.templateService.clearOptions(excelElement);
        } else {
            excelElement.id = (count + 1).toString();
            excelElement.pimFields.push(new NextPimField(element, system, field));

            if (this.exportMode) {
                excelElement.title = field;
                if (system != null && system >= 0) {
                    excelElement.title += '_' + system;
                    if (field != null && field >= 0) {
                        excelElement.title += '_' + field;
                    }
                }
            }

            this.templateService.clearPreviewRow1Row2(excelElement);
            this.excelModel.push(excelElement);
            this.excelModel = [...this.excelModel];
        }

        if (field == this.fieldNames.descriptionLong) {
            // bei dem HTML Editor kommt es nicht in e.value mit...
            value = await this.templateService.getValueFromProduct(
                this.product,
                false,
                false,
                excelElement,
                field,
                system,
                element,
                '|'
            );
        } else if (!this.isUpdateModeField(field)) {
            if (value == null || value == undefined) {
                value = '';
            } else {
                value = await this.templateService.getValueFromProduct(
                    this.product,
                    false,
                    false,
                    excelElement,
                    field,
                    system,
                    element,
                    '|'
                );
            }
        }

        this.templateService.setOptions(excelElement, value, '', '', '', '|');
        this.updateTemplate(field);
    }

    public async addElement(field: string, system: string, element: string) {
        let myDialog: any;
        let txtYes = '';
        let txtNo = '';
        let txtMsg = '';
        let txtTitle = '';

        if (!this.templateService.template) {
            this.systemService.notify(new Message(this.messageSelectTemplate, 'info'), 3000);
            return;
        }

        if (!this.exportMode) {
            return;
        }

        if (this.templateService.template.isImportTemplate) {
            let pimFields = this.templateService.template.templateItems.flatMap(item => item.pimFields);

            let nextPimFields = pimFields.filter(x => x.field == field);
            if (nextPimFields.length > 0) {
                this.translate.get('Ja').subscribe((text: string) => (txtYes = text));
                this.translate.get('Nein').subscribe((text: string) => (txtNo = text));
                this.translate.get('Felder werden mehrmals verknüpft, das Template kann dann nicht mehr für den Import genutzt werden, möchten Sie dies wirklich?').subscribe((text: string) => {
                    txtMsg = text;
                    if (!txtMsg.endsWith('?')) txtMsg = `${txtMsg}?`;
                });
                this.translate.get('Wirklich verknüpfen?').subscribe((text: string) => (txtTitle = text));

                myDialog = custom({
                    title: txtTitle,
                    messageHtml: txtMsg,
                    buttons: [
                        {text: txtYes, onClick: () => 'yes'},
                        {text: txtNo, onClick: () => 'no'}
                    ]
                });
                const result = await myDialog.show();
                if (result === 'no')
                    return;

                this.templateService.template.isImportTemplate = false;
            }
        }
        this.templateService.refresExcelElementhOrder(this.excelModel);
        let count = this.excelModel.length;
        let excelElement = new ExcelElement();
        excelElement.id = (count + 1).toString();

        excelElement.title = field;
        let result = await lastValueFrom(
            this.templateService.getHeadline(field, element, system, this.modelService.loginService.currentCustomer.id)
        );
        excelElement.title = result.value;
        var existing = this.excelModel.filter(
            (x) =>
                x.pimFields &&
                x.pimFields.some(
                    (pimfied) => pimfied.field == field && pimfied.systemKey == system && pimfied.elementKey == element
                )
        );
        if (existing && existing.length > 1) {
            excelElement.title = excelElement.title + ' (' + existing.length + ')';
        }
        let featureOrderStr: string = null;
        if (field.startsWith(this.fieldNames.benefitPrefix)) {
            featureOrderStr = field.substring(this.fieldNames.benefitPrefix.length);
        } else if (field.startsWith(this.fieldNames.topFeaturePrefix)) {
            featureOrderStr = field.substring(this.fieldNames.topFeaturePrefix.length);
        }

        if (featureOrderStr != null) {
            excelElement.title = field;
            excelElement.name = featureOrderStr;
            excelElement.pimFields = [new NextPimField(element, system, field)];

            if (this.exportMode) {
                let value = await this.templateService.getValueFromProduct(
                    this._product,
                    true,
                    true,
                    excelElement,
                    field,
                    system,
                    element
                );
                excelElement.preview = value;
                excelElement.row1 = value;
            }

            this.excelModel.push(excelElement);
            this.excelModel = [...this.excelModel];
            this.updateTemplate(field);
            return;
        }

        var existing = this.excelModel.filter(
            (x) =>
                x.pimFields &&
                x.pimFields.some(
                    (pimfied) => pimfied.field == field && pimfied.systemKey == system && pimfied.elementKey == element
                )
        );
        if (existing && existing.length > 1) {
            excelElement.title = excelElement.title + ' (' + existing.length + ')';
        }

        excelElement.pimFields = [new NextPimField(element, system, field)];

        if (this.exportMode) {
            let value = await this.templateService.getValueFromProduct(
                this.product,
                true,
                true,
                excelElement,
                field,
                system,
                element /*, excelElement.seperator*/
            );
            excelElement.preview = value;
            excelElement.row1 = value;
        }

        this.excelModel.push(excelElement);
        this.excelModel = [...this.excelModel];
        this.updateTemplate(field);
    }

    public async addElementFromExcel(field: string, systemKey: string, elementKey: string, item: ExcelElement) {
        if (field.startsWith(this.fieldNames.benefitPrefix)) {
            item.name = field.substring(this.fieldNames.benefitPrefix.length);
        } else if (field.startsWith(this.fieldNames.topFeaturePrefix)) {
            item.name = field.substring(this.fieldNames.topFeaturePrefix.length);
        }
        if (!this.modelService.loginService.hasAddonMultipleImportTemplateFields) {
            await this.clearPimField(new NextPimField(elementKey, systemKey, field), false);
        }

        if (item.defaultValue != '') {
            if (!this.isUpdateModeField(field)) {
                await this.templateService.setValueOnProduct(
                    this.product,
                    false,
                    false,
                    item,
                    field,
                    systemKey,
                    elementKey,
                    item.defaultValue,
                    this.excelModel,
                    null,
                    true
                );
            }
        } else {
            await this.templateService.setValueOnProduct(
                this.product,
                false,
                false,
                item,
                field,
                systemKey,
                elementKey,
                item.row1,
                this.excelModel,
                null,
                true
            );
        }

        item.pimFields.push(new NextPimField(elementKey, systemKey, field));

        if (this.exportMode) {
            this.templateService.clearPreviewRow1Row2(item);
        }

        await this.updateTemplate(field);

        this.selectedElements = this.templateService.createIdentifiers(item.pimFields);
    }

    public removeElement(field: string, system: string, element: string, item: ExcelElement) {
        const fromIndex = this.excelModel.indexOf(item);

        if (field == undefined && system == undefined && element == undefined && fromIndex >= 0) {
            this.excelModel.splice(fromIndex, 1);
            this.excelModel = [...this.excelModel];
        } else {
            let found = this.excelModel.find(
                (i) => i.pimFields.findIndex((x) => x.field == field && x.systemKey == system && x.elementKey == element) != -1
            );

            let indexRight = this.excelModel.indexOf(found);
            if (indexRight >= 0) {
                this.excelModel.splice(indexRight, 1);
                this.excelModel = [...this.excelModel];
            }
        }

        this.updateTemplate(field);
    }

    public async clearItem(e) {
        let matching = this.excelModel.filter((element) => element.id == e);

        for (let item of matching) {
            if (this.exportMode) {
                this.templateService.clearPreviewRow1Row2(item);

                for await (const x of item.pimFields) {
                    let oldValue = await this.templateService.getValueFromProduct(
                        this.backupProduct,
                        false,
                        true,
                        item,
                        x.field,
                        x.systemKey,
                        x.elementKey,
                        item.seperator
                    );
                    await this.templateService.setValueOnProduct(
                        this.product,
                        false,
                        true,
                        item,
                        x.field,
                        x.systemKey,
                        x.elementKey,
                        oldValue,
                        this.excelModel,
                        item.seperator,
                        true
                    );
                }
            } else {
                for (const x of item.pimFields) {
                    await this.templateService.setValueOnProduct(
                        this.product,
                        false,
                        false,
                        item,
                        x.field,
                        x.systemKey,
                        x.elementKey,
                        null,
                        this.excelModel,
                        null,
                        true
                    );
                }
            }
            item.pimFields = [];

            this.excelModel = [...this.excelModel];

            this.templateService.clearOptions(item);
            this.updateTemplate(item.pimFields.map((x) => x.field));
            this.selectedElements = [];
        }
    }

    public async clearPimField(pimField: NextPimField, needRefresh: boolean = true) {
        const isIndividualBenefit = pimField.field.startsWith(this.fieldNames.benefitPrefix);
        const isIndividualTopFeature = pimField.field.startsWith(this.fieldNames.topFeaturePrefix);
        if (isIndividualBenefit || isIndividualTopFeature) {
            const fieldKey = pimField.field;

            let matchingExcelItem = this.excelModel.filter((x) =>
                x.pimFields.some(y => y.field == fieldKey)
            );
            for (let excelItem of matchingExcelItem) {
                if (this.exportMode) {
                    this.templateService.clearPreviewRow1Row2(excelItem);
                }
                if (!this.exportMode) {
                    excelItem.pimFields = excelItem.pimFields.filter(y => y.field != fieldKey);
                    excelItem.name = '';
                } else {
                    excelItem.pimFields = [];
                }
                this.templateService.clearOptions(excelItem);
            }

            this.excelModel = this.excelModel.filter((x) => x.pimFields.length > 0 || (x.title != null && x.title != '' && x.title != fieldKey));
            this.excelModel = [...this.excelModel];

            this.context.pimFieldCleared.emit();

            if (needRefresh) await this.updateTemplate(fieldKey);
            return;
        }

        let isFeatureKey =
            pimField.field == this.fieldNames.fName ||
            pimField.field == this.fieldNames.featureSystemName ||
            pimField.field == this.fieldNames.fUnit;

        let matchingExcelItem = this.excelModel.filter((x) =>
            x.pimFields.some(
                (y) =>
                    (isFeatureKey || y.field == pimField.field) &&
                    y.elementKey == pimField.elementKey &&
                    y.systemKey == pimField.systemKey
            )
        );

        for (let excelItem of matchingExcelItem) {
            let pimFieldsToRemove = excelItem.pimFields.filter(
                (y) =>
                    (isFeatureKey || y.field == pimField.field) &&
                    y.elementKey == pimField.elementKey &&
                    y.systemKey == pimField.systemKey
            );

            if (this.exportMode) {
                this.templateService.clearPreviewRow1Row2(excelItem);

                for await (const x of pimFieldsToRemove) {
                    let oldValue = await this.templateService.getValueFromProduct(
                        this.backupProduct,
                        false,
                        true,
                        excelItem,
                        x.field,
                        x.systemKey,
                        x.elementKey,
                        excelItem.seperator
                    );
                    await this.templateService.setValueOnProduct(
                        this.product,
                        false,
                        true,
                        excelItem,
                        x.field,
                        x.systemKey,
                        x.elementKey,
                        oldValue,
                        this.excelModel,
                        excelItem.seperator,
                        true
                    );
                }
            } else {
                for (const x of pimFieldsToRemove) {
                    await this.templateService.setValueOnProduct(
                        this.product,
                        false,
                        false,
                        excelItem,
                        x.field,
                        x.systemKey,
                        x.elementKey,
                        null,
                        this.excelModel,
                        null,
                        true
                    );
                }
            }

            excelItem.pimFields = excelItem.pimFields.filter(
                (y) =>
                    !(
                        (isFeatureKey || y.field == pimField.field) &&
                        y.elementKey == pimField.elementKey &&
                        y.systemKey == pimField.systemKey
                    )
            );
            this.templateService.clearOptions(excelItem);
        }

        this.excelModel = this.excelModel.filter((x) => x.pimFields.length > 0 || (x.title != null && x.title != ''));

        if (needRefresh) await this.updateTemplate(pimField.field);
    }

    public async replaceItem(e) {
        const targetGrid = e.component;
        const keyOfFirstVisibleRow = targetGrid.getKeyByRowIndex(0);
        const listIndexOfFirstVisibleRow = this.excelModel.findIndex(x => x.id === keyOfFirstVisibleRow);
        let i = listIndexOfFirstVisibleRow + e.toIndex;
        if (i == this.excelModel.length) {
            i--; // beim letzten item sowohl DROP davor als auch danach zulassen
        }

        if (
            this.templateService.hasExcelElement(this.excelModel, e.itemData.dField, e.itemData.dSystem, e.itemData.dElement)
        ) {
            let existing = this.templateService.getExcelElement(
                this.excelModel,
                e.itemData.dField,
                e.itemData.dSystem,
                e.itemData.dElement
            );
            let index = this.excelModel.indexOf(existing);

            this.excelModel[index].pimFields = this.excelModel[index].pimFields.filter(
                (x) =>
                    !(x.elementKey == e.itemData.dElement && x.systemKey == e.itemData.dSystem && x.field == e.itemData.dField)
            );
            this.excelModel = [...this.excelModel];

            if (this.exportMode) {
                this.templateService.clearPreviewRow1Row2(this.excelModel[index]);
            }

            this.templateService.clearOptions(this.excelModel[index]);
        }

        this.excelModel[i].pimFields.push(new NextPimField(e.itemData.dElement, e.itemData.dSystem, e.itemData.dField));
        this.excelModel = [...this.excelModel];

        if (this.exportMode) this.templateService.clearPreviewRow1Row2(this.excelModel[i]);

        this.templateService.clearOptions(this.excelModel[i]);
        this.updateTemplate(e.itemData.dField);
    }

    public reorderModel(e) {
        if (!this.exportMode) return;

        const grid = e.component;

        const keyOfFirstVisibleRow = grid.getKeyByRowIndex(0);
        const absFirstVisible = this.excelModel.findIndex(x => x.id === keyOfFirstVisibleRow);

        if (absFirstVisible < 0) return;

        let absoluteToIndex = absFirstVisible + e.toIndex;
        if (absoluteToIndex > this.excelModel.length)
            absoluteToIndex = this.excelModel.length;

        const movedId = e.itemData?.id;
        const fromIndex = this.excelModel.findIndex(x => x.id === movedId);
        if (fromIndex < 0)
            return;

        const [item] = this.excelModel.splice(fromIndex, 1);

        if (absoluteToIndex > fromIndex)
            absoluteToIndex -= 1;

        absoluteToIndex = Math.max(0, Math.min(absoluteToIndex, this.excelModel.length));
        this.excelModel.splice(absoluteToIndex, 0, item);

        this.excelModel = [...this.excelModel];

        this.updateTemplate(e.itemData?.dField);
    }

    public async updateTemplate(field: string | string[]) {
        const fields = Array.isArray(field) ? field : [field];
        const items = this.excelModel.filter((x) => x.pimFields.some((y) => fields.includes(y.field)));
        if (this.exportMode) {
            // nur im EXPORTMODE!! SONST KOMMT DAS AUS DEM EXCEL-TEMPLATE!!

            for (let item of items) {
                // "datenschrott" verhindern:
                // tritt auf, wenn ein geleertes oder gefülltes feld ohne seperator nun mit diesen feldern gefüllt wird
                // die array-elemente werden dann mit "" gejoined, aus der nummer kommen wir dann später nicht mehr 'raus...
                // sollte das der falsche seperator sein, kommt man an den ursprünglichen wert immer noch aus dem backupProduct 'ran
                item.pimFields.forEach(async (field) => {
                    if (this.templateService.needsStandardSeperator(field.field)) {
                        if (item.seperator == null || item.seperator == undefined || item.seperator == '') {
                            item.seperator = '|';
                        }
                    }

                    // diese sind noch nicht aktualisiert, werden in getValueFromProduct aber verwendet!!
                    this.templateService.template.templateItems = this.templateService.createTemplateItems(
                        this.excelModel,
                        this.exportMode
                    );

                    let value = await this.templateService.getValueFromProduct(
                        this.product,
                        true,
                        true,
                        item,
                        field.field,
                        field.systemKey,
                        field.elementKey,
                        item.seperator
                    );

                    item.preview = value;
                    item.row1 = value;
                });

                item.updateImportFieldUsed();
            }
        }

        this.templateService.template.isExportTemplate = this.exportMode;
        this.templateService.template.templateItems = this.templateService.createTemplateItems(
            this.excelModel,
            this.exportMode
        );
        this.templateService.updateTemplate(this.templateService.template);

        // Das ist nicht nötig (wir haben daraus grade das Template erzeugt & gespeichert)
        // UND es führt zu dem unerwünschten Verhalten s. Ticket https://nextpim.myjetbrains.com/youtrack/issue/nPIM-789
        //this.excelModel = this.templateService.createExcelElements(this.templateService.template.templateItems, this.templateService.template.isUseable);

        for (let item of items) {
            if (this.exportMode) {
                // hier nicht - produkt im exportmode nicht verändern
                //this.templateService.setValueOnProduct(this.product, true, !this.exportMode, item, item.field, item.system, item.element, item.row1, item.seperator, true);
                //this.templateService.setValueOnProduct(this.product, false, this.exportMode, item, item.field, item.system, item.element, item.row1, item.seperator, true);
            } else {
                if (item.defaultValue != '') {
                    // brauchen wir gar nicht. wert passt ja schon.
                    // bei einigen Controls wird eine Endlosschleife ausgelöst...
                    //let value = await this.templateService.getValueFromProduct(this.product, true, this.exportMode, item, item.field, item.system, item.element, item.seperator);
                    //if (item.defaultValue != value) {
                    //  this.templateService.setValueOnProduct(this.product, true, this.exportMode, item, item.field, item.system, item.element, item.defaultValue, item.seperator, true);
                    //}
                    // UPDATEMODE TODO HERE ????
                } else {
                    // Bei neuen Elementen wurde der Seperator (naturgemäß) noch gar nicht gesetzt
                    // Strings werden bei "" gesplittet:
                    // Aus 1 z.B.Status "blau" werden 4 Status "b" "l" "a" "u"

                    for (const x of item.pimFields) {
                        if (this.templateService.needsStandardSeperator(x.field)) {
                            if (item.seperator == null || item.seperator == undefined || item.seperator == '') {
                                item.seperator = '|';
                            }
                        }
                        await this.templateService.setValueOnProduct(
                            this.product,
                            true,
                            this.exportMode,
                            item,
                            x.field,
                            x.systemKey,
                            x.elementKey,
                            item.row1,
                            this.excelModel,
                            item.seperator,
                            true
                        );
                    }
                }

                //this.templateService.setValueOnProduct(this.product, true, this.exportMode, item, item.field, item.system, item.element, item.row1, item.seperator, true);
            }
        }

        this.excelModel = [...this.excelModel];
        this.context.excelModel = this.excelModel;
        this.context.templateUpdated.emit();
    }

    public templateSelected(e) {
        this.isVisible = true;
        this.fillProducts();

        if (this.templateService.template) {
            if (!this.templateService?.template?.useMetadata) {
                this.modelService.catalogService.useMetadata = null;
            } else {
                this.modelService.catalogService.useMetadata = true;
            }
            this.excelModel = this.templateService.createExcelElements(
                this.templateService.template.templateItems,
                this.templateService.template.isUseable
            );

            this.excelModel.forEach((item) => {
                item.pimFields.forEach(async (x) => {
                    if (item.defaultValue != '') {
                        if (!this.isUpdateModeField(x.field)) {
                            await this.templateService.setValueOnProduct(
                                this.product,
                                true,
                                this.exportMode,
                                item,
                                x.field,
                                x.systemKey,
                                x.elementKey,
                                item.defaultValue,
                                this.excelModel,
                                item.seperator,
                                true
                            );
                        }
                    } else {
                        await this.templateService.setValueOnProduct(
                            this.product,
                            true,
                            this.exportMode,
                            item,
                            x.field,
                            x.systemKey,
                            x.elementKey,
                            item.row1,
                            this.excelModel,
                            item.seperator,
                            true
                        );
                        this.templateService.template.isUseable = true;
                    }
                });
            });
        } else {
            this.excelModel = [];
        }
        this.isVisible = false;
        this.context.excelModel = this.excelModel;
        this.context.templateUpdated.emit();
    }

    private isUpdateModeField(field: string): boolean {
        if (
            field == this.fieldNames.featureUpdateMode ||
            field == this.fieldNames.mimeUpdateMode ||
            field == this.fieldNames.priceUpdateMode ||
            field == this.fieldNames.udxUpdateMode ||
            field == this.fieldNames.supplierUpdateMode ||
            field == this.fieldNames.statusUpdateMode ||
            field == this.fieldNames.categoryUpdateMode ||
            field == this.fieldNames.packingUpdateMode
        ) {
            return true;
        }
        return false;
    }

    private buildListsAndTables() {
        if (this.product == null || this.product == undefined) {
            return;
        }

        this.product.featureSystems.forEach((item) => {
            item.featureSystemList = this.templateService.createListForSystem(item);
            item.featureSystemTable = this.templateService.createTableForSystem(item);
        });

        this.product.featurelist = this.templateService.createTableForAllSystems(this.product.featureSystems);
        this.product.descWithoutHTML = Functions.stripHTML(this.product.descriptionLong);
        this.product.groupString = this.templateService.createGroupString(this.excelModel);
    }
}
